import {
  FallOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  RiseOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { mainText, secondaryRed, tertiaryGreen } from 'assets/styles/variables'
import {
  formatUtc,
  toUtcDayjs,
  utcEndOfDay,
  utcStartOfDay,
} from 'components/date-range-picker/utc-date-utils'
import { ResizableHeaderCell } from 'features/charts-v2/components/ResizableHeaderCell/ResizableHeaderCell'
import { useChartQuery } from 'features/charts-v2/hooks/use-chart-query'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import {
  augmentMetricsForWeighting,
  computeWeightedTotals,
  type MetricValues,
} from 'features/charts-v2/strategies/summary-totals'
import { BREAKDOWNS, METRICS, type MetricType } from 'features/charts-v2/types'
import {
  type ChartData,
  ChartProps,
  type SegmentClickPayload,
  TransformedChartData,
  type TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
// bully ignore[shared-imports-features: rule's applies predicate is always_ts_file, not scoped to configured shared_layer_paths - same-feature import (charts-v2 importing its own charts-v2 ChartPanel type, the canonical path 25 files use). follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { aliasChartDataForDisplay } from 'features/charts-v2/utils/format-breakdown-value'
import { resolveCategoryMappingAxis } from 'features/charts-v2/utils/summary-utils'
import { useProjectStore } from 'features/project/store'
import { ProjectState } from 'features/project/types'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

interface TableRow {
  key: string
  category: string
  breakdownValues: string[]
  displayLabel?: string
  rowSpans: number[]
  groupIndex: number
  color?: string
  parentIndex?: number
  children?: TableRow[]
  [key: string]:
    | string
    | number
    | null
    | string[]
    | number[]
    | TableRow[]
    | undefined
}

const BLUE = {
  100: '#f1f6fd',
  200: '#e0ebfa',
  300: '#c6dcf7',
}
const WHITE = {
  0: '#ffffff',
  100: '#f5f5f5',
  200: '#e6e6e6',
  300: '#d3d3d3',
}

const COLORS = {
  parentRowWhite: {
    base: WHITE['0'],
    hover: WHITE['100'],
  },
  parentRowBlue: {
    base: BLUE['100'],
    hover: BLUE['200'],
  },
  childRowWhite: {
    base: WHITE['100'],
    hover: WHITE['200'],
  },
  childRowBlue: {
    base: BLUE['200'],
    hover: BLUE['300'],
  },
}

// padding-bottom leaves room for the page-size Select's 2px focus ring, which
// is drawn outside its box and would otherwise be clipped by the
// overflow: hidden wrapper this footer sits in.
const PaginationFooter = styled.div`
  flex-shrink: 0;
  padding-top: 12px;
  padding-bottom: 4px;
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);

  .anticon {
    font-size: 12px;
  }

  .summary-metrics-page-size {
    width: 80px;
  }
`

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PAGE_SIZE_PRESETS = [10, 20, 50, 100]
const MAX_PAGE_SIZE = 1000

const toPageSizeOption = (n: number): { value: number; label: string } => ({
  value: n,
  label: String(n),
})

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component
const TablePaginationFooter: React.FC<{
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}> = ({ total, page, pageSize, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(page, totalPages)
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1
  const end = Math.min(safePage * pageSize, total)

  const canGoFirst = safePage > 1
  const canGoPrev = safePage > 1
  const canGoNext = safePage < totalPages
  const canGoLast = safePage < totalPages

  // Type-to-search lets the user enter any page size, not only the presets.
  const [search, setSearch] = useState('')
  const pageSizeOptions = useMemo(() => {
    if (search === '') {
      return [...new Set([...PAGE_SIZE_PRESETS, pageSize])]
        .sort((a, b) => a - b)
        .map(toPageSizeOption)
    }
    const typed = Number(search)
    const typedIsValid = typed >= 1 && typed <= MAX_PAGE_SIZE
    const matchingPresets = PAGE_SIZE_PRESETS.filter(
      (n) => String(n).includes(search) && n !== typed,
    )
    return [...(typedIsValid ? [typed] : []), ...matchingPresets].map(
      toPageSizeOption,
    )
  }, [search, pageSize])

  return (
    <>
      <FooterRow>
        <FooterLeft>
          <span>Rows per page:</span>
          <Select
            className="summary-metrics-page-size"
            value={pageSize}
            options={pageSizeOptions}
            searchable
            searchValue={search}
            onSearch={(text) => setSearch(text.replace(/\D/g, ''))}
            filterOption={false}
            notFoundContent={`Enter 1–${MAX_PAGE_SIZE}`}
            onChange={(size) => {
              onPageSizeChange(size)
              onPageChange(1)
              setSearch('')
            }}
            onBlur={() => setSearch('')}
          />
        </FooterLeft>
        <FooterRight>
          <span style={{ marginRight: 8 }}>
            {start}–{end} of {total.toLocaleString()}
          </span>
          <Button
            size="xs"
            variant="text"
            aria-label="First page"
            icon={<VerticalRightOutlined />}
            disabled={!canGoFirst}
            onClick={() => onPageChange(1)}
          />
          <Button
            size="xs"
            variant="text"
            aria-label="Previous page"
            icon={<LeftOutlined />}
            disabled={!canGoPrev}
            onClick={() => onPageChange(safePage - 1)}
          />
          <Button
            size="xs"
            variant="text"
            aria-label="Next page"
            icon={<RightOutlined />}
            disabled={!canGoNext}
            onClick={() => onPageChange(safePage + 1)}
          />
          <Button
            size="xs"
            variant="text"
            aria-label="Last page"
            icon={<VerticalLeftOutlined />}
            disabled={!canGoLast}
            onClick={() => onPageChange(totalPages)}
          />
        </FooterRight>
      </FooterRow>
    </>
  )
}

const StyledTable = styled(Table<TableRow>)`
  /* The styled-component class lands on .ant-table-wrapper. Without explicit
     height + flex here, every "height: 100%" rule below resolves to auto and
     pagination ends up below the panel's overflow:hidden clip. */
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;

  .ant-table {
    border: 1px solid #d8d8d8;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-row {
    cursor: pointer;
  }

  .ant-table-column-sort {
    background: none;
  }

  .ant-table-thead th {
    background: ${WHITE['100']} !important;
  }

  /* tableLayout="fixed" (needed so a dragged-narrower column width sticks
     instead of the table re-growing to fit content) means a long value no
     longer stretches its column — wrap it inside the column's width instead. */
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    overflow-wrap: anywhere;
  }

  .ant-table-thead th:first-child {
    text-transform: capitalize;
  }

  .ant-table-thead th.ant-table-column-sort {
    background: ${WHITE['100']} !important;
  }

  .ant-table-row-expand-icon {
    cursor: pointer;
  }

  /* Child rows: move the indent from sibling DOM (.ant-table-row-indent and
     .ant-table-row-expand-icon-spaced) onto the content div as padding-left.
     The sibling elements only position the first line; wrapped lines wrap
     to the td's padding edge, breaking visual alignment within the cell.
     Putting the indent on the div instead means wrapped lines wrap inside
     the indented content area and align with the first line. */
  .ant-table-row-level-1 .ant-table-row-indent,
  .ant-table-row-level-1 .ant-table-row-expand-icon-spaced {
    display: none;
  }
  .ant-table-row-level-1 > td.ant-table-cell:first-child > div {
    padding-left: 24px;
  }

  .parent-row-even {
    & > td {
      background-color: ${COLORS.parentRowWhite.base} !important;
      transition: background-color 0.2s ease;
    }

    &:hover > td {
      background-color: ${COLORS.parentRowWhite.hover} !important;
    }
  }

  .parent-row-odd {
    & > td {
      background-color: ${COLORS.parentRowBlue.base} !important;
      transition: background-color 0.2s ease;
    }

    &:hover > td {
      background-color: ${COLORS.parentRowBlue.hover} !important;
    }
  }

  .child-row-even {
    & > td {
      background-color: ${COLORS.childRowWhite.base} !important;
      transition: background-color 0.2s ease;
    }

    &:hover > td {
      background-color: ${COLORS.childRowWhite.hover} !important;
    }
  }

  .child-row-odd {
    & > td {
      background-color: ${COLORS.childRowBlue.base} !important;
      transition: background-color 0.2s ease;
    }

    &:hover > td {
      background-color: ${COLORS.childRowBlue.hover} !important;
    }
  }

  .ant-table-summary {
    .summary-row > td {
      background-color: ${WHITE['100']} !important;
      font-weight: 600;
      border-top: 2px solid #999;
      transition: background-color 0.2s ease;
    }

    .summary-row:hover > td {
      background-color: ${WHITE['200']} !important;
    }

    .subtotal-row > td {
      background-color: ${WHITE['100']} !important;
      font-weight: 500;
      border-top: 1px solid #bbb;
      transition: background-color 0.2s ease;
    }

    .subtotal-row:hover > td {
      background-color: ${WHITE['200']} !important;
    }
  }

  /* When scroll.y is set, .ant-table-header / .ant-table-body /
     .ant-table-summary are siblings inside .ant-table-container (a flex
     column). Pin header + summary at their natural height; the body grows
     to fill whatever space remains (flex: 1 + max-height: none overrides
     antd's own scroll.y max-height, which otherwise only clamps a tall body
     and never stretches a short one) and scrolls internally if it overflows. */
  .ant-table-header {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .ant-table-body {
    flex: 1;
    min-height: 0;
    max-height: none !important;
    overflow: auto !important;
  }

  .ant-table-summary {
    flex: 0 0 auto;
  }

  .ant-table-container {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  /* Make the spin container a flex column so the pagination footer becomes a
     real flex sibling of .ant-table instead of overflowing past its bottom. */
  .ant-spin-nested-loading,
  .ant-spin-nested-loading > .ant-spin-container {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table-content {
    display: flex;
    flex-direction: column;
  }
`

/**
 * Calculate previous period dates based on current date range.
 * Shift both start and end back by the duration, then normalize to UTC
 * midnight (start) and UTC end-of-day (end).
 */
function calculatePreviousPeriodDates(
  startDate: Date | null,
  endDate: Date | null,
): { startDate: Date; endDate: Date } | null {
  if (!startDate || !endDate) {
    return null
  }

  const startSec = Math.floor(startDate.getTime() / 1000)
  const endSec = Math.floor(endDate.getTime() / 1000)
  const timeDiff = Math.abs(endSec - startSec)

  // Shift both dates back by the full duration
  const prevStartRaw = new Date((startSec - timeDiff) * 1000)
  const prevEndRaw = new Date((endSec - timeDiff) * 1000)

  // Normalize to UTC midnight (start) and UTC end-of-day (end)
  const prevStartDate = utcStartOfDay(toUtcDayjs(prevStartRaw)).toDate()
  const prevEndDate = utcEndOfDay(toUtcDayjs(prevEndRaw)).toDate()

  return {
    startDate: prevStartDate,
    endDate: prevEndDate,
  }
}

/**
 * Hook to fetch previous period chart data for comparison.
 *
 * Driven by the effective `panel` passed in (via ChartProps) rather than the
 * charts-v2 panel-id context / store, so the trend columns work wherever a
 * caller supplies a panel — the charts-v2 builder AND dashboard tiles
 * (PROD-4025). The previous period reuses the full panel (same
 * filters/metrics/breakdowns/search) with only `dateRange` shifted back one
 * period, so the comparison is apples-to-apples with the current period.
 */
function usePreviousPeriodChartQuery(
  projectId: string | null,
  panel: ChartPanel | null | undefined,
): {
  previousData: ChartData | null | undefined
  isPreviousLoading: boolean
  previousPeriodDates: { startDate: Date; endDate: Date } | null
} {
  // Calculate previous period dates (timestamps in seconds)
  const previousPeriodDates = useMemo(() => {
    if (!panel) return null
    return calculatePreviousPeriodDates(
      new Date(panel.dateRange.startDate * 1000), // Convert to Date
      new Date(panel.dateRange.endDate * 1000),
    )
  }, [panel])

  // Create modified panel with previous period dates
  const previousPanel = useMemo(() => {
    if (!previousPeriodDates || !panel) return null

    return {
      ...panel,
      dateRange: {
        startDate: Math.floor(previousPeriodDates.startDate.getTime() / 1000),
        endDate: Math.floor(previousPeriodDates.endDate.getTime() / 1000),
      },
    }
  }, [panel, previousPeriodDates])

  // Fetch previous period data
  const { data: previousData, isLoading: isPreviousLoading } = useChartQuery(
    projectId,
    previousPanel ?? panel ?? null,
  )

  // If no panel supplied, return nulls
  if (!panel) {
    return {
      previousData: null,
      isPreviousLoading: false,
      previousPeriodDates: null,
    }
  }

  return {
    previousData: previousPeriodDates ? previousData : null,
    isPreviousLoading,
    previousPeriodDates,
  }
}

/**
 * Build a lookup map from category key to metric values
 * For multi-breakdown scenarios, concatenate all breakdown values with a delimiter
 */
function buildCategoryLookup(
  chartData: ChartData | null,
  numBreakdowns: number = 1,
): Map<string, (number | null)[]> {
  const lookup = new Map<string, (number | null)[]>()

  if (!chartData || !chartData.data) {
    return lookup
  }

  chartData.data.forEach((item) => {
    // Build category key from all breakdown levels
    const categoryKey = item.category
      .slice(0, numBreakdowns)
      .map((c) => String(c || ALIAS_FOR_NULL_VALUES))
      .join('|||') // Use delimiter unlikely to appear in actual values

    lookup.set(categoryKey, item.values)
  })

  // Also include parent_data if available (for backward compatibility)
  if (chartData.parent_data) {
    chartData.parent_data.forEach((item) => {
      const categoryKey = item.category
        .slice(0, numBreakdowns)
        .map((c) => String(c || ALIAS_FOR_NULL_VALUES))
        .join('|||')

      lookup.set(categoryKey, item.values)
    })
  }

  return lookup
}

/**
 * Calculate percentage change or absolute difference for trends
 */
function calculateChange(
  current: number,
  previous: number,
  metric: MetricType,
): number | null {
  if (previous === 0 || previous == null || current == null) {
    return null
  }

  // For sentiment, use absolute difference instead of percentage
  if (metric === METRICS.SENTIMENT.id) {
    return current - previous
  }

  // For all other metrics, calculate percentage change
  return Math.round(((current - previous) / previous) * 100)
}

/**
 * TrendIndicator component to display trend with icon and tooltip
 */
// eslint-disable-next-line react-refresh/only-export-components -- internal helper component, not a public export
const TrendIndicator: React.FC<{
  current: number
  previous: number | null
  metric: MetricType
  isLoading: boolean
  previousPeriodDates: { startDate: Date; endDate: Date } | null
}> = ({ current, previous, metric, isLoading, previousPeriodDates }) => {
  if (isLoading) {
    return <LoadingOutlined style={{ fontSize: '12px', color: '#999' }} />
  }

  if (previous == null || previous === 0) {
    return <span style={{ color: '#999', fontSize: '12px' }}>-</span>
  }

  const change = calculateChange(current, previous, metric)

  if (change == null || change === Infinity || isNaN(change)) {
    return <span style={{ color: '#999', fontSize: '12px' }}>-</span>
  }

  const isSentiment = metric === METRICS.SENTIMENT.id
  const color =
    change === 0 ? mainText : change > 0 ? tertiaryGreen : secondaryRed

  const formatChange = (value: number): string => {
    if (isSentiment) {
      return value.toFixed(2)
    }
    return value.toLocaleString()
  }

  const tooltipContent = previousPeriodDates ? (
    <div style={{ fontSize: '12px' }}>
      Compared to {previous.toLocaleString()} from{' '}
      {formatUtc(previousPeriodDates.startDate)} to{' '}
      {formatUtc(previousPeriodDates.endDate)}
    </div>
  ) : (
    <div style={{ fontSize: '12px' }}>
      Compared to {previous.toLocaleString()} (previous period)
    </div>
  )

  return (
    <Tooltip content={tooltipContent}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          justifyContent: 'flex-end',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            fontWeight: '400',
            color,
          }}
        >
          {change >= 0 && '+'}
          {formatChange(change)}
          {!isSentiment && '%'}
        </span>
        {change === 0 ? (
          <span style={{ color: mainText }}>-</span>
        ) : change > 0 ? (
          <RiseOutlined style={{ color: tertiaryGreen, fontSize: '12px' }} />
        ) : (
          <FallOutlined style={{ color: secondaryRed, fontSize: '12px' }} />
        )}
      </div>
    </Tooltip>
  )
}

export class SummaryMetricsChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.TABLE
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  /**
   * Check if a metric is summable (can calculate % of total)
   */
  private isSummableMetric(metric: MetricType): boolean {
    return (
      metric === METRICS.RECORD_COUNT.id || metric === METRICS.SENTENCE_COUNT.id
    )
  }

  /**
   * Get formatted metric label for column header
   */
  private getMetricColumnLabel(metric: MetricType): string {
    switch (metric) {
      case METRICS.RECORD_COUNT.id:
        return 'Records'
      case METRICS.SENTENCE_COUNT.id:
        return 'Sentences'
      case METRICS.SENTIMENT.id:
        return 'Sentiment'
      case METRICS.RATING.id:
        return 'Rating'
      case METRICS.NET_PROMOTER_SCORE.id:
        return 'NPS'
      default:
        return metric
    }
  }

  public transform(props: ChartProps): TransformedChartData {
    const { data, selectedBreakdowns = [] } = props

    if (!data || !data.data) {
      return { chartData: [] }
    }

    const numBreakdowns = selectedBreakdowns.length

    if (numBreakdowns === 0) {
      // No breakdowns - simple aggregation
      const row: TableRow = {
        key: 'total',
        category: 'Total',
        breakdownValues: ['Total'],
        rowSpans: [1],
        groupIndex: 0,
        parentIndex: 0,
      }

      if (data.data.length > 0) {
        data.data[0]!.values.forEach((value, valueIndex) => {
          row[`metric_${valueIndex}`] = value
        })
      }

      return {
        chartData: [row] as unknown as TransformedDataPoint[],
        metadata: {
          breakdownNames: ['Category'],
        },
      }
    }

    // Get breakdown names for metadata
    const breakdownNames = selectedBreakdowns.map((bd) => {
      if (typeof bd === 'string') {
        const breakdownConfig = Object.values(BREAKDOWNS).find(
          (b) => b.id === bd,
        )
        return breakdownConfig?.label || bd
      }
      if (bd?.kind === 'custom') {
        return bd.alias || 'Breakdown'
      }
      if (bd?.kind === 'standard') {
        const breakdownConfig = Object.values(BREAKDOWNS).find(
          (b) => b.id === bd.id,
        )
        return breakdownConfig?.label || bd.id
      }
      return 'Breakdown'
    })

    // Check for grouping mode (brand/product/theme grouping)
    // Only use expandable grouping for single breakdown
    const hasGrouping =
      numBreakdowns === 1 &&
      data.parent_data &&
      data.parent_data.length > 0 &&
      data.category_mapping &&
      Object.keys(data.category_mapping).length > 0

    // Handle single breakdown with grouping (expandable parent/child rows)
    if (hasGrouping) {
      // category_mapping is keyed per axis by wire Dimension column string;
      // resolve the single grouped breakdown's axis to its bucket→children
      // dict before building the expandable rows (PROD-3451).
      const axisMapping = resolveCategoryMappingAxis(
        data.category_mapping!,
        selectedBreakdowns[0]!,
      )
      const tableRows = this.buildGroupedRowsWithChildren(
        data.data,
        data.parent_data!,
        axisMapping,
        breakdownNames[0] || 'Category',
      )
      return {
        chartData: tableRows as unknown as TransformedDataPoint[],
        metadata: {
          breakdownNames,
          hasGrouping: true,
        },
      }
    }

    // For multiple breakdowns OR non-grouped single breakdown:
    // Build flat pivot table with rowSpan
    // The data source (parent_data vs data) is already chosen by Container based on toggle
    const dataToUse =
      numBreakdowns > 1 && data.parent_data && data.parent_data.length > 0
        ? data.parent_data
        : data.data

    const tableRows = this.buildPivotTableRowsWithRowSpan(
      dataToUse,
      numBreakdowns,
    )

    return {
      chartData: tableRows as unknown as TransformedDataPoint[],
      metadata: {
        breakdownNames,
        hasGrouping: false,
      },
    }
  }

  /**
   * Build pivot table rows with rowSpan (flat structure, no nested children)
   * Each row represents the innermost breakdown level
   * Outer breakdowns use rowSpan to group rows visually
   */
  private buildPivotTableRowsWithRowSpan(
    dataItems: Array<{
      category: Array<string | number>
      values: Array<number | null>
    }>,
    numBreakdowns: number,
  ): TableRow[] {
    if (numBreakdowns === 1) {
      // Single breakdown - simple flat structure
      return dataItems.map((item, index) => {
        const row: TableRow = {
          key: `row-${index}`,
          category: String(item.category[0] || ALIAS_FOR_NULL_VALUES),
          breakdownValues: [String(item.category[0] || ALIAS_FOR_NULL_VALUES)],
          rowSpans: [1],
          groupIndex: index,
          parentIndex: index,
        }

        item.values.forEach((value, valueIndex) => {
          row[`metric_${valueIndex}`] = value
        })

        return row
      })
    }

    // Multi-level breakdowns - build hierarchical grouping with rowSpan
    interface GroupNode {
      value: string
      children: Map<string, GroupNode>
      dataItems: Array<{
        category: Array<string | number>
        values: Array<number | null>
      }>
    }

    const root: GroupNode = {
      value: '',
      children: new Map(),
      dataItems: [],
    }

    // Build tree structure
    dataItems.forEach((item) => {
      let currentNode = root

      for (let level = 0; level < numBreakdowns; level++) {
        const value = String(item.category[level] || ALIAS_FOR_NULL_VALUES)

        if (!currentNode.children.has(value)) {
          currentNode.children.set(value, {
            value,
            children: new Map(),
            dataItems: [],
          })
        }

        currentNode = currentNode.children.get(value)!
      }

      // Store at leaf level
      currentNode.dataItems.push(item)
    })

    // Sort leaf nodes by first metric (descending)
    const sortLeafNodes = (node: GroupNode): void => {
      // If this node has data items (leaf), sort them by first metric
      if (node.dataItems.length > 0) {
        node.dataItems.sort((a, b) => {
          const aValue = Number(a.values[0] ?? 0)
          const bValue = Number(b.values[0] ?? 0)
          return bValue - aValue // Descending (larger numbers first)
        })
      }

      // Recursively sort children
      node.children.forEach((childNode) => {
        sortLeafNodes(childNode)
      })
    }

    sortLeafNodes(root)

    // Flatten tree to rows with rowSpan calculations
    const rows: TableRow[] = []
    let groupIndex = 0

    const flattenNode = (
      node: GroupNode,
      level: number,
      parentBreakdownValues: string[],
      parentGroupIndex: number,
    ): number => {
      if (level === numBreakdowns) {
        // Leaf node - create row from data items (already sorted)
        node.dataItems.forEach((item) => {
          const row: TableRow = {
            key: `row-${rows.length}`,
            category: parentBreakdownValues[0] || ALIAS_FOR_NULL_VALUES,
            breakdownValues: parentBreakdownValues,
            // Initialize rowSpans: all set to 0, will be updated for visible cells
            rowSpans: new Array(numBreakdowns).fill(0),
            groupIndex: parentGroupIndex,
            parentIndex: parentGroupIndex,
          }

          // Add metric values
          item.values.forEach((value, valueIndex) => {
            row[`metric_${valueIndex}`] = value
          })

          rows.push(row)
        })
        return node.dataItems.length
      }

      // Internal node - recurse to children
      let totalChildRows = 0

      // Sort children by their total metric value (sum of all leaf dataItems)
      const childEntries = Array.from(node.children.entries())
      childEntries.sort((a, b) => {
        const [, aNode] = a
        const [, bNode] = b

        // Calculate total first metric for this child branch
        const getTotalMetric = (n: GroupNode): number => {
          let total = 0
          n.dataItems.forEach((item) => {
            total += Number(item.values[0] ?? 0)
          })
          n.children.forEach((child) => {
            total += getTotalMetric(child)
          })
          return total
        }

        const aTotal = getTotalMetric(aNode)
        const bTotal = getTotalMetric(bNode)
        return bTotal - aTotal // Descending
      })

      childEntries.forEach(([value, childNode]) => {
        const breakdownValues = [...parentBreakdownValues, value]
        const currentGroupIndex = level === 0 ? groupIndex++ : parentGroupIndex

        // Track where this child's rows start
        const startRowIdx = rows.length

        const childRowCount = flattenNode(
          childNode,
          level + 1,
          breakdownValues,
          currentGroupIndex,
        )

        // Set rowSpan for this level's column on the first row of this child
        // This makes each child span only its own descendant rows
        if (
          childRowCount > 0 &&
          startRowIdx < rows.length &&
          rows[startRowIdx]
        ) {
          rows[startRowIdx]!.rowSpans[level] = childRowCount
        }

        totalChildRows += childRowCount
      })

      return totalChildRows
    }

    // Start flattening from root
    const rootEntries = Array.from(root.children.entries())
    rootEntries.forEach(([value, childNode]) => {
      const currentGroupIndex = groupIndex++
      const childRowCount = flattenNode(
        childNode,
        1,
        [value],
        currentGroupIndex,
      )

      // Set rowSpan for first column
      if (childRowCount > 0) {
        const firstRowIdx = rows.length - childRowCount
        if (firstRowIdx >= 0 && rows[firstRowIdx]) {
          rows[firstRowIdx]!.rowSpans[0] = childRowCount
        }
      }
    })

    return rows
  }

  /**
   * Build grouped rows with expandable children for single breakdown
   * Uses parent_data for parent rows and data for child rows
   * Children are attached via the children property for Ant Design expandable tables
   */
  private buildGroupedRowsWithChildren(
    dataItems: Array<{
      category: Array<string | number>
      values: Array<number | null>
    }>,
    parentData: Array<{
      category: Array<string | number>
      values: Array<number | null>
    }>,
    categoryMapping: Record<string, string[]>,
    breakdownName: string,
  ): TableRow[] {
    const tableRows: TableRow[] = []

    // Determine if this is a theme breakdown for special formatting
    const isThemeBreakdown =
      breakdownName?.toLowerCase() === 'theme' ||
      breakdownName?.toLowerCase() === 'parent theme' ||
      breakdownName?.toLowerCase() === 'cluster_topic' ||
      breakdownName?.toLowerCase() === 'cluster topic'

    // Build parent rows from parent_data
    parentData.forEach((parentItem, parentIndex) => {
      const parentCategory = String(
        parentItem.category[0] || ALIAS_FOR_NULL_VALUES,
      )
      const row: TableRow = {
        key: `parent-${parentIndex}`,
        category: parentCategory,
        breakdownValues: [parentCategory],
        rowSpans: [1],
        groupIndex: parentIndex,
        parentIndex: parentIndex,
        color: undefined,
      }

      // Add metric values to parent row
      parentItem.values.forEach((value, valueIndex) => {
        row[`metric_${valueIndex}`] = value
      })

      // Find children for this parent
      const childCategories = categoryMapping[parentCategory]
      if (childCategories && childCategories.length > 0) {
        const children: TableRow[] = []

        // Find child rows from main data
        dataItems.forEach((childItem, childIndex) => {
          const childCategory = String(
            childItem.category[0] || ALIAS_FOR_NULL_VALUES,
          )
          if (childCategories.includes(childCategory)) {
            // For theme breakdown, strip the parent prefix if present
            let displayCategory = childCategory
            if (isThemeBreakdown && displayCategory.includes(': ')) {
              displayCategory =
                displayCategory.split(': ')[1] || displayCategory
            }

            const childRow: TableRow = {
              key: `child-${parentIndex}-${childIndex}`,
              category: childCategory,
              breakdownValues: [childCategory],
              displayLabel: displayCategory,
              rowSpans: [1],
              groupIndex: parentIndex,
              parentIndex: parentIndex,
              color: undefined,
            }

            // Add metric values to child row
            childItem.values.forEach((value, valueIndex) => {
              childRow[`metric_${valueIndex}`] = value ?? 0
            })

            children.push(childRow)
          }
        })

        // Only add children if there are any
        // For theme breakdown, only add if there are multiple children
        if (isThemeBreakdown && children.length > 1) {
          row.children = children
        } else if (!isThemeBreakdown && children.length > 0) {
          row.children = children
        }
      }

      tableRows.push(row)
    })

    return tableRows
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      selectedMetrics,
      selectedBreakdowns = [],
      onSegmentClick,
      panel,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    const tableData = transformedData.chartData as TableRow[]
    const breakdownNames = (transformedData.metadata?.[
      'breakdownNames'
    ] as string[]) || ['Category']
    const hasGrouping =
      (transformedData.metadata?.['hasGrouping'] as boolean) || false

    return (
      <SummaryMetricsTableContainer
        data={tableData}
        selectedMetrics={selectedMetrics}
        selectedBreakdowns={selectedBreakdowns}
        breakdownNames={breakdownNames}
        hasGrouping={hasGrouping}
        getMetricColumnLabel={this.getMetricColumnLabel}
        isSummableMetric={this.isSummableMetric}
        onSegmentClick={onSegmentClick}
        panel={panel}
      />
    )
  }

  public getTitle(): string {
    return 'Summary Metrics Table'
  }
}

/**
 * Recalculate rowSpans for a flat array of rows based on their breakdownValues.
 * This is necessary after sorting to maintain visual grouping with rowSpan.
 *
 * For each breakdown level i, we track when breakdownValues[0..i] changes.
 * The first row of each group gets rowSpan = count, subsequent rows get 0.
 */
function recalculateRowSpans(
  rows: TableRow[],
  numBreakdowns: number,
): TableRow[] {
  if (rows.length === 0) return rows

  // Clone rows to avoid mutation
  const result = rows.map((row) => ({
    ...row,
    rowSpans: new Array(numBreakdowns).fill(0),
  }))

  // For each breakdown level, track group boundaries
  for (let level = 0; level < numBreakdowns; level++) {
    let groupStartIdx = 0
    let currentGroupKey = result[0]?.breakdownValues
      .slice(0, level + 1)
      .join('|||')

    for (let i = 1; i <= result.length; i++) {
      const isLastRow = i === result.length
      const nextGroupKey = isLastRow
        ? null
        : result[i]?.breakdownValues.slice(0, level + 1).join('|||')

      // Check if we've reached the end of a group
      if (isLastRow || nextGroupKey !== currentGroupKey) {
        const groupSize = i - groupStartIdx
        // Set rowSpan on the first row of the group
        if (result[groupStartIdx]) {
          result[groupStartIdx]!.rowSpans[level] = groupSize
        }
        // All other rows in the group already have rowSpan = 0

        if (!isLastRow && nextGroupKey !== null) {
          groupStartIdx = i
          currentGroupKey = nextGroupKey
        }
      }
    }
  }

  return result
}

function getPercentColumnLabels(metric: MetricType): {
  volume: string
  parent: string
} {
  switch (metric) {
    case METRICS.RECORD_COUNT.id:
      return { volume: '% of Records', parent: '% of Parent Records' }
    case METRICS.SENTENCE_COUNT.id:
      return { volume: '% of Sentences', parent: '% of Parent Sentences' }
    default:
      return { volume: '% Volume', parent: '% Volume (Parent)' }
  }
}

/**
 * Container component that fetches previous period data and passes to table
 */
// eslint-disable-next-line react-refresh/only-export-components -- internal helper component, not a public export
const SummaryMetricsTableContainer: React.FC<{
  data: TableRow[]
  selectedMetrics: MetricType[]
  selectedBreakdowns: unknown[]
  breakdownNames: string[]
  hasGrouping: boolean
  getMetricColumnLabel: (metric: MetricType) => string
  isSummableMetric: (metric: MetricType) => boolean
  onSegmentClick?: (
    segmentData: SegmentClickPayload,
    event: React.MouseEvent,
  ) => void
  panel?: ChartPanel
}> = (props) => {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)

  // Fetch previous period data for trend comparison, driven by the effective
  // panel passed via ChartProps (not the charts-v2 panel-id context / store).
  const { previousData, isPreviousLoading, previousPeriodDates } =
    usePreviousPeriodChartQuery(projectId, props.panel)

  // The Change / % Change trend columns need a panel to compute the
  // previous-period comparison (the query shifts the panel's date range).
  // Callers that supply a panel (the charts-v2 builder and dashboard tiles) get
  // the columns; standalone renders — e.g. an Ask Yogi chart artifact — pass no
  // panel and omit them rather than show empty "–" columns (PROD-4025).
  const showTrendColumns = props.panel != null

  return (
    <SummaryMetricsTable
      {...props}
      previousData={previousData}
      isPreviousLoading={isPreviousLoading}
      previousPeriodDates={previousPeriodDates}
      showTrendColumns={showTrendColumns}
    />
  )
}

/**
 * Separate component for the table rendering to use hooks
 */
// eslint-disable-next-line react-refresh/only-export-components -- internal helper component, not a public export
const SummaryMetricsTable: React.FC<{
  data: TableRow[]
  selectedMetrics: MetricType[]
  selectedBreakdowns: unknown[]
  breakdownNames: string[]
  hasGrouping: boolean
  getMetricColumnLabel: (metric: MetricType) => string
  isSummableMetric: (metric: MetricType) => boolean
  onSegmentClick?: (
    segmentData: SegmentClickPayload,
    event: React.MouseEvent,
  ) => void
  previousData?: ChartData | null
  isPreviousLoading?: boolean
  previousPeriodDates?: { startDate: Date; endDate: Date } | null
  showTrendColumns?: boolean
}> = ({
  data,
  selectedMetrics,
  selectedBreakdowns,
  breakdownNames,
  hasGrouping,
  getMetricColumnLabel,
  isSummableMetric,
  onSegmentClick,
  previousData,
  isPreviousLoading,
  previousPeriodDates,
  showTrendColumns = true,
}) => {
  const numBreakdowns = selectedBreakdowns.length || 1

  // Group-based pagination state (for pivot tables with multiple breakdowns)
  const [currentPage, setCurrentPage] = useState(1)
  const [groupPageSize, setGroupPageSize] = useState(100)

  // Ant Design pagination state (for single-breakdown / grouped tables)
  const [antdCurrentPage, setAntdCurrentPage] = useState(1)
  const [antdPageSize, setAntdPageSize] = useState(100)

  // Sort state for multi-breakdown tables
  const [sortState, setSortState] = useState<{
    columnKey: string
    order: 'ascend' | 'descend'
  } | null>({ columnKey: 'metric_0', order: 'descend' })

  // User-dragged column widths, keyed by column key. Session-only — not
  // persisted to the panel or filter hash.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  // Reset Ant Design pagination to page 1 when sorting changes.
  // Depend on primitive values, not the sortState object reference, so a plain
  // page click (which recreates the sortState object with the same values) does
  // not clobber antdCurrentPage back to 1.
  React.useEffect(() => {
    setAntdCurrentPage(1)
  }, [sortState?.columnKey, sortState?.order])

  // Build lookup map for previous period values.
  // Alias the previous-period data so its category keys match the aliased keys in the
  // current-period rows (both become "Verified" instead of "True"), preventing Change/%
  // Change column misses for boolean breakdowns.
  const previousLookup = useMemo(() => {
    const aliasedPrevious = previousData
      ? aliasChartDataForDisplay(previousData, selectedBreakdowns)
      : null
    return buildCategoryLookup(aliasedPrevious, numBreakdowns)
  }, [previousData, selectedBreakdowns, numBreakdowns])

  // The positional metric list the backend response is keyed by — the user's
  // selection plus any hidden weight metric appended LAST (e.g. sentence_count
  // for sentiment). Both `use-chart-query` (request seam) and the totals math
  // resolve weight columns against this same augmented list, so the hidden
  // weight's index is consistent across the wire and here. The rendered columns,
  // "% of Total", and CSV all iterate `selectedMetrics`, so the hidden metric
  // never surfaces in the UI.
  const metricsForData = useMemo(
    () => augmentMetricsForWeighting(selectedMetrics),
    [selectedMetrics],
  )

  // Apply sorting for multi-breakdown tables while preserving group structure
  const sortedData = useMemo(() => {
    if (!sortState) {
      return data
    }

    const { columnKey, order } = sortState
    const isAscending = order === 'ascend'

    // Determine if sorting by breakdown or metric
    const breakdownMatch = columnKey.match(/^breakdown_(\d+)$/)
    const metricMatch = columnKey.match(/^metric_(\d+)$/)

    // Handle single-breakdown sorting (simple flat array sort)
    if (numBreakdowns <= 1) {
      const compare = (a: TableRow, b: TableRow): number => {
        if (breakdownMatch) {
          const idx = parseInt(breakdownMatch[1] || '0', 10)
          const aVal = a.breakdownValues[idx] || ''
          const bVal = b.breakdownValues[idx] || ''
          const cmp = String(aVal).localeCompare(String(bVal))
          return isAscending ? cmp : -cmp
        }
        if (metricMatch) {
          const aVal = (a[columnKey] as number) || 0
          const bVal = (b[columnKey] as number) || 0
          return isAscending ? aVal - bVal : bVal - aVal
        }
        return 0
      }

      const sorted = [...data].sort(compare)

      // Reassign groupIndex for alternating row colors (including children)
      // and sort children by the same comparator so expanded rows follow
      // the same sort direction as their parent.
      return sorted.map((row, index) => {
        const updatedRow = {
          ...row,
          groupIndex: index,
        }

        // If this row has children, sort and update their groupIndex too
        if (row.children) {
          updatedRow.children = [...row.children]
            .sort(compare)
            .map((child) => ({ ...child, groupIndex: index }))
        }

        return updatedRow
      })
    }

    if (breakdownMatch) {
      // Sorting by a breakdown column
      const breakdownLevel = parseInt(breakdownMatch[1] || '0', 10)

      // Group rows by all HIGHER-level breakdowns (0..breakdownLevel-1)
      const groups = new Map<string, TableRow[]>()
      data.forEach((row) => {
        const groupKey =
          breakdownLevel > 0
            ? row.breakdownValues.slice(0, breakdownLevel).join('|||')
            : '__root__'
        if (!groups.has(groupKey)) {
          groups.set(groupKey, [])
        }
        groups.get(groupKey)!.push(row)
      })

      // Sort within each group by the target breakdown value
      const sortedRows: TableRow[] = []
      groups.forEach((groupRows) => {
        const sorted = [...groupRows].sort((a, b) => {
          const aVal = a.breakdownValues[breakdownLevel] || ''
          const bVal = b.breakdownValues[breakdownLevel] || ''
          const comparison = String(aVal).localeCompare(String(bVal))
          return isAscending ? comparison : -comparison
        })
        sortedRows.push(...sorted)
      })

      // Recalculate rowSpans after sorting
      const withRowSpans = recalculateRowSpans(sortedRows, numBreakdowns)

      // Reassign groupIndex for pagination and row striping
      let currentGroupIndex = 0
      let lastPrimaryValue = withRowSpans[0]?.breakdownValues[0]
      return withRowSpans.map((row) => {
        if (row.breakdownValues[0] !== lastPrimaryValue) {
          currentGroupIndex++
          lastPrimaryValue = row.breakdownValues[0]
        }
        return { ...row, groupIndex: currentGroupIndex }
      })
    } else if (metricMatch) {
      // Sorting by a metric column
      const metricKey = columnKey

      // Recursive tree build: group at each breakdown level, sort by aggregate metric
      const buildAndSort = (rows: TableRow[], level: number): TableRow[] => {
        if (level >= numBreakdowns) {
          // Leaf: sort individual rows by metric
          return [...rows].sort((a, b) => {
            const aVal = (a[metricKey] as number) || 0
            const bVal = (b[metricKey] as number) || 0
            return isAscending ? aVal - bVal : bVal - aVal
          })
        }

        // Group by breakdown value at this level
        const subGroups = new Map<string, TableRow[]>()
        rows.forEach((row) => {
          const key = row.breakdownValues[level] || ALIAS_FOR_NULL_VALUES
          if (!subGroups.has(key)) subGroups.set(key, [])
          subGroups.get(key)!.push(row)
        })

        // Recursively sort children, then sort groups by aggregate
        const sorted = Array.from(subGroups.entries()).map(([key, subRows]) => {
          const sortedRows = buildAndSort(subRows, level + 1)
          const total = sortedRows.reduce(
            (sum, r) => sum + ((r[metricKey] as number) || 0),
            0,
          )
          return { key, rows: sortedRows, total }
        })

        sorted.sort((a, b) =>
          isAscending ? a.total - b.total : b.total - a.total,
        )

        return sorted.flatMap((g) => g.rows)
      }

      const sortedRows = buildAndSort(data, 0)
      const withRowSpans = recalculateRowSpans(sortedRows, numBreakdowns)

      // Reassign groupIndex based on primary breakdown
      let currentGroupIndex = 0
      let lastPrimaryValue = withRowSpans[0]?.breakdownValues[0]
      return withRowSpans.map((row) => {
        if (row.breakdownValues[0] !== lastPrimaryValue) {
          currentGroupIndex++
          lastPrimaryValue = row.breakdownValues[0]
        }
        return { ...row, groupIndex: currentGroupIndex }
      })
    }

    return data
  }, [data, sortState, numBreakdowns])

  // Group-based pagination for pivot tables (multiple breakdowns)
  const groupPagination = useMemo(() => {
    if (numBreakdowns <= 1) return null

    // Build map: groupIndex -> rows[]
    const groupMap = new Map<number, TableRow[]>()
    sortedData.forEach((row) => {
      const idx = row.groupIndex
      if (!groupMap.has(idx)) {
        groupMap.set(idx, [])
      }
      groupMap.get(idx)!.push(row)
    })

    const groupIndices = Array.from(groupMap.keys()).sort((a, b) => a - b)
    const totalGroups = groupIndices.length

    return { groupMap, groupIndices, totalGroups }
  }, [sortedData, numBreakdowns])

  // Compute visible rows based on current page (for group-based pagination)
  const { visibleData, totalGroups } = useMemo(() => {
    if (!groupPagination) {
      return { visibleData: sortedData, totalGroups: sortedData.length }
    }

    const { groupMap, groupIndices, totalGroups } = groupPagination
    const startGroup = (currentPage - 1) * groupPageSize
    const endGroup = Math.min(startGroup + groupPageSize, totalGroups)
    const visibleGroupIndices = groupIndices.slice(startGroup, endGroup)

    const visibleData = visibleGroupIndices.flatMap(
      (idx) => groupMap.get(idx) || [],
    )
    return { visibleData, totalGroups }
  }, [groupPagination, currentPage, groupPageSize, sortedData])

  const hasMultiplePages =
    numBreakdowns > 1 && !hasGrouping
      ? totalGroups > groupPageSize
      : sortedData.length > antdPageSize

  // Compute page-visible data for subtotal calculations
  const pageVisibleData = useMemo(() => {
    if (numBreakdowns > 1 && !hasGrouping) {
      return visibleData
    }
    const start = (antdCurrentPage - 1) * antdPageSize
    return sortedData.slice(start, start + antdPageSize)
  }, [
    numBreakdowns,
    hasGrouping,
    visibleData,
    sortedData,
    antdCurrentPage,
    antdPageSize,
  ])

  // Calculate totals for each metric for percentage calculations
  const metricTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    selectedMetrics.forEach((_, index) => {
      const key = `metric_${index}`
      let total = 0

      // Sum top-level rows only. In grouped single-breakdown mode each parent
      // row's value is already the roll-up of its children, so recursing into
      // children would double the denominator and halve every row's percentage.
      data.forEach((row) => {
        total += (row[key] as number) || 0
      })

      totals[key] = total
    })
    return totals
  }, [data, selectedMetrics])

  // Calculate totals for parent rows only (to avoid double-counting in hierarchical views)
  const parentTotals = useMemo(() => {
    const rows: MetricValues[] = data.map((row) =>
      metricsForData.map(
        (_, index) => (row[`metric_${index}`] as number | null) ?? null,
      ),
    )
    return computeWeightedTotals(rows, metricsForData)
  }, [data, metricsForData])

  // Calculate subtotals for current page only
  const pageSubtotals = useMemo(() => {
    const rows: MetricValues[] = pageVisibleData.map((row) =>
      metricsForData.map(
        (_, index) =>
          (row as unknown as Record<string, number | null>)[
            `metric_${index}`
          ] ?? null,
      ),
    )
    return computeWeightedTotals(rows, metricsForData)
  }, [pageVisibleData, metricsForData])

  // Calculate previous period totals for trend comparison
  const previousPeriodTotals = useMemo(() => {
    const rows: MetricValues[] = Array.from(previousLookup.values())
    return computeWeightedTotals(rows, metricsForData)
  }, [previousLookup, metricsForData])

  // Build columns dynamically based on selected breakdowns and metrics
  const columns = useMemo<ColumnsType<TableRow>>(() => {
    const cols: ColumnsType<TableRow> = []

    // Build a lookup from groupIndex → parent row so child rows can compute
    // their share of the parent group. Only populated when hasGrouping is true.
    const parentRowByGroupIndex = new Map<number, TableRow>()
    if (hasGrouping) {
      data.forEach((row) => {
        parentRowByGroupIndex.set(row.groupIndex, row)
      })
    }

    // Add a column for each breakdown with rowSpan support
    breakdownNames.forEach((breakdownName, breakdownIndex) => {
      const columnKey = `breakdown_${breakdownIndex}`
      const width = columnWidths[columnKey] ?? 150

      cols.push({
        title: breakdownName,
        dataIndex: 'breakdownValues',
        key: columnKey,
        width,
        fixed: breakdownIndex === 0 ? 'left' : undefined,
        sorter: () => 0,
        sortOrder: sortState?.columnKey === columnKey ? sortState.order : null,
        onHeaderCell: () => ({
          width,
          onResize: (next: number) =>
            setColumnWidths((prev) => ({ ...prev, [columnKey]: next })),
        }),
        onCell: (record: TableRow) => {
          const rowSpan = record.rowSpans[breakdownIndex]
          // rowSpan of 0 means this cell is merged into a previous row
          // Don't render it at all
          if (rowSpan === 0) {
            return { rowSpan: 0 }
          }
          return {
            rowSpan: rowSpan || 1,
            // Align first and second breakdown columns to top when they span multiple rows
            style:
              (breakdownIndex === 0 || breakdownIndex === 1) &&
              (rowSpan || 1) > 1
                ? { verticalAlign: 'top' }
                : {},
          }
        },
        render: (breakdownValues: string[], record: TableRow) => {
          const rowSpan = record.rowSpans[breakdownIndex]

          // If rowSpan is 0, this cell shouldn't be rendered (merged with previous)
          // Return undefined to let Ant Design handle it
          if (rowSpan === 0) {
            return undefined
          }

          const value = breakdownValues[breakdownIndex] || ALIAS_FOR_NULL_VALUES
          // Use the stripped display label for grouped theme children; fall back
          // to the identity value (which IS the display text for all other rows).
          const display = record.displayLabel ?? value

          // Render with color dot if available (for first breakdown only)
          if (breakdownIndex === 0 && record.color) {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  cursor: onSegmentClick ? 'pointer' : 'default',
                }}
                onClick={(event) => {
                  if (onSegmentClick) {
                    // Get the breakdown values up to and including this level
                    const clickedBreakdownValues = record.breakdownValues.slice(
                      0,
                      breakdownIndex + 1,
                    )
                    // Get the first metric value
                    const metricValue = (record['metric_0'] as number) || 0

                    onSegmentClick(
                      {
                        primaryBreakdown: value,
                        value: metricValue,
                        payload: record as Record<
                          string,
                          string | number | boolean | null
                        >,
                        breakdownValues: clickedBreakdownValues,
                        isGroupedChild: record.key
                          ?.toString()
                          .startsWith('child-'),
                      },
                      event,
                    )
                  }
                }}
              >
                <div
                  style={{
                    minWidth: '30px',
                    height: '30px',
                    backgroundColor: record.color as string,
                    marginRight: '10px',
                    borderRadius: 10,
                  }}
                />
                <div>{display}</div>
              </div>
            )
          }

          return (
            <div
              style={{
                cursor: onSegmentClick ? 'pointer' : 'default',
              }}
              onClick={(event) => {
                if (onSegmentClick) {
                  // Get the breakdown values up to and including this level
                  const clickedBreakdownValues = record.breakdownValues.slice(
                    0,
                    breakdownIndex + 1,
                  )
                  // Get the first metric value
                  const metricValue = (record['metric_0'] as number) || 0

                  onSegmentClick(
                    {
                      primaryBreakdown: value,
                      value: metricValue,
                      payload: record as Record<
                        string,
                        string | number | boolean | null
                      >,
                      breakdownValues: clickedBreakdownValues,
                      isGroupedChild: record.key
                        ?.toString()
                        .startsWith('child-'),
                    },
                    event,
                  )
                }
              }}
            >
              {display}
            </div>
          )
        },
      })
    })

    // Add columns for each selected metric
    selectedMetrics.forEach((metric, index) => {
      const metricKey = `metric_${index}`
      const metricLabel = getMetricColumnLabel(metric)

      // Add metric value column
      const metricWidth = columnWidths[metricKey] ?? 120
      cols.push({
        title: metricLabel,
        dataIndex: metricKey,
        key: metricKey,
        align: 'right',
        width: metricWidth,
        sorter: () => 0,
        sortOrder: sortState?.columnKey === metricKey ? sortState.order : null,
        onHeaderCell: () => ({
          width: metricWidth,
          onResize: (next: number) =>
            setColumnWidths((prev) => ({ ...prev, [metricKey]: next })),
        }),
        render: (value: number | null) => {
          if (value == null) return ALIAS_FOR_NULL_VALUES
          // Format numbers appropriately
          if (Number.isInteger(value)) {
            return value.toLocaleString()
          }
          return value.toFixed(2)
        },
      })

      // Add trend column after metric value — only where comparison data is
      // available (inside a charts-v2 panel). Standalone renders omit it.
      if (showTrendColumns) {
        const trendLabel =
          metric === METRICS.SENTIMENT.id ? 'Change' : '% Change'
        const trendColumnKey = `${metricKey}_trend`
        const trendWidth = columnWidths[trendColumnKey] ?? 110
        cols.push({
          title: (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                justifyContent: 'flex-end',
              }}
            >
              {trendLabel}
              {previousPeriodDates && (
                <Tooltip
                  content={`Compared to previous period: ${formatUtc(previousPeriodDates.startDate)} to ${formatUtc(previousPeriodDates.endDate)}`}
                >
                  <InfoCircleOutlined style={{ fontSize: 12, color: '#999' }} />
                </Tooltip>
              )}
            </span>
          ),
          key: trendColumnKey,
          align: 'right',
          width: trendWidth,
          onHeaderCell: () => ({
            width: trendWidth,
            onResize: (next: number) =>
              setColumnWidths((prev) => ({ ...prev, [trendColumnKey]: next })),
          }),
          render: (_: unknown, row: TableRow) => {
            const currentValue = row[metricKey] as number | null
            if (currentValue == null) return null

            // Look up previous value by concatenated breakdown values.
            // breakdownValues carries the real wire identity for all rows
            // (including grouped theme children), so no fallback needed.
            const categoryKey = row.breakdownValues.join('|||')
            const previousValues = previousLookup.get(categoryKey)
            const previousValue = previousValues ? previousValues[index] : null

            return (
              <TrendIndicator
                current={currentValue}
                previous={previousValue ?? null}
                metric={metric}
                isLoading={isPreviousLoading ?? false}
                previousPeriodDates={previousPeriodDates ?? null}
              />
            )
          },
        })
      }

      // Add % of total volume column for summable metrics
      if (isSummableMetric(metric)) {
        const { volume: volumeLabel, parent: parentLabel } =
          getPercentColumnLabels(metric)
        const percentColumnKey = `${metricKey}_percent`
        const percentWidth = columnWidths[percentColumnKey] ?? 100
        cols.push({
          title: volumeLabel,
          key: percentColumnKey,
          align: 'right',
          width: percentWidth,
          onHeaderCell: () => ({
            width: percentWidth,
            onResize: (next: number) =>
              setColumnWidths((prev) => ({
                ...prev,
                [percentColumnKey]: next,
              })),
          }),
          render: (_: unknown, row: TableRow) => {
            const value = row[metricKey] as number | null
            if (value == null) return '-'
            const total = metricTotals[metricKey] || 0
            if (total === 0) return '0%'
            const percent = ((value / total) * 100).toFixed(1)
            return `${percent}%`
          },
        })

        // Add % of parent group column — only in expandable grouped view
        if (hasGrouping) {
          const percentParentColumnKey = `${metricKey}_percent_parent`
          const percentParentWidth = columnWidths[percentParentColumnKey] ?? 130
          cols.push({
            title: parentLabel,
            key: percentParentColumnKey,
            align: 'right',
            width: percentParentWidth,
            onHeaderCell: () => ({
              width: percentParentWidth,
              onResize: (next: number) =>
                setColumnWidths((prev) => ({
                  ...prev,
                  [percentParentColumnKey]: next,
                })),
            }),
            render: (_: unknown, row: TableRow) => {
              const isChildRow = row.key?.toString().startsWith('child-')
              if (!isChildRow) return '-'

              const value = row[metricKey] as number | null
              if (value == null) return '-'

              const parentRow = parentRowByGroupIndex.get(row.parentIndex ?? -1)
              const parentValue = parentRow?.[metricKey] as number | null
              if (parentValue == null || parentValue === 0) return '-'

              const percent = ((value / parentValue) * 100).toFixed(1)
              const metricNoun = getMetricColumnLabel(metric).toLowerCase()
              const chicletStyle: React.CSSProperties = {
                display: 'inline-block',
                padding: '0 6px',
                borderRadius: 4,
                backgroundColor: '#555',
                color: '#fff',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }
              return (
                <Tooltip
                  content={
                    <span style={{ fontSize: '12px' }}>
                      <span style={chicletStyle}>
                        {row.displayLabel ?? row.category}
                      </span>
                      {`'s ${value.toLocaleString()} ${metricNoun} account for ${percent}% of `}
                      <span style={chicletStyle}>
                        {parentRow?.displayLabel ?? parentRow?.category}
                      </span>
                      {`'s ${parentValue.toLocaleString()} ${metricNoun}`}
                    </span>
                  }
                >
                  <span>{percent}%</span>
                </Tooltip>
              )
            },
          })
        }
      }
    })

    return cols
  }, [
    breakdownNames,
    selectedMetrics,
    getMetricColumnLabel,
    isSummableMetric,
    metricTotals,
    previousLookup,
    isPreviousLoading,
    previousPeriodDates,
    showTrendColumns,
    sortState,
    onSegmentClick,
    hasGrouping,
    data,
    columnWidths,
  ])

  // Total table width — drives scroll.x so tableLayout="fixed" keeps every
  // dragged column width instead of re-growing the table to fit content.
  const totalWidth = useMemo(
    () => columns.reduce((sum, col) => sum + (Number(col.width) || 0), 0),
    [columns],
  )

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{ flex: 1, minHeight: 0, overflow: 'hidden', height: '100%' }}
      >
        <StyledTable
          dataSource={pageVisibleData}
          columns={columns}
          // y is a nominal value — it only triggers antd's scroll-mode DOM
          // (separate header/body/summary siblings); the actual body height
          // comes from StyledTable's flex CSS, not from this value (see
          // TeamMembersTable.tsx for the same pattern).
          // x is the summed column width, not 'max-content' — paired with
          // tableLayout="fixed" below so a dragged-narrower column width
          // sticks instead of the table re-growing to fit cell content.
          scroll={{ x: totalWidth, y: 1 }}
          tableLayout="fixed"
          components={{ header: { cell: ResizableHeaderCell } }}
          pagination={false}
          onChange={(_, __, sorter) => {
            // Handle sorting
            if (!Array.isArray(sorter)) {
              if (sorter.order && sorter.columnKey) {
                setSortState({
                  columnKey: sorter.columnKey as string,
                  order: sorter.order as 'ascend' | 'descend',
                })
              } else {
                setSortState(null)
              }
            }
          }}
          expandable={
            hasGrouping
              ? {
                  indentSize: 24,
                  defaultExpandAllRows: false,
                }
              : undefined
          }
          rowClassName={(record: TableRow) => {
            // For grouped tables with expandable rows
            if (hasGrouping) {
              const isEven = (record.groupIndex ?? 0) % 2 === 0
              const isChild =
                record.children === undefined &&
                record.key?.toString().startsWith('child-')

              if (isChild) {
                return isEven ? 'child-row-even' : 'child-row-odd'
              }
              return isEven ? 'parent-row-even' : 'parent-row-odd'
            }

            // For non-grouped tables
            const isEven = (record.groupIndex ?? 0) % 2 === 0
            return isEven ? 'parent-row-even' : 'parent-row-odd'
          }}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              {hasMultiplePages && (
                <Table.Summary.Row
                  className="subtotal-row"
                  style={{ fontWeight: '500' }}
                >
                  <Table.Summary.Cell index={0}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      Subtotal
                      <Tooltip content="Subtotal for this page of the dataset">
                        <InfoCircleOutlined
                          style={{ fontSize: 12, color: '#999' }}
                        />
                      </Tooltip>
                    </span>
                  </Table.Summary.Cell>
                  {breakdownNames.length > 1 && (
                    <Table.Summary.Cell
                      index={1}
                      colSpan={breakdownNames.length - 1}
                    />
                  )}
                  {selectedMetrics.map((metric, index) => {
                    const metricKey = `metric_${index}`
                    const subtotal = pageSubtotals[metricKey]

                    // Calculate base index for this metric — same column-block
                    // accounting as the Total row. The trend column is dropped
                    // when showTrendColumns is false, so offsets shift down.
                    const trendCols = showTrendColumns ? 1 : 0
                    let baseIndex = breakdownNames.length
                    for (let i = 0; i < index; i++) {
                      const prevMetric = selectedMetrics[i]
                      if (prevMetric) {
                        const summableCols = (hasGrouping ? 3 : 2) + trendCols
                        baseIndex += isSummableMetric(prevMetric)
                          ? summableCols
                          : 1 + trendCols
                      }
                    }
                    const volumeOffset = 1 + trendCols
                    const parentOffset = 2 + trendCols

                    return (
                      <React.Fragment key={metricKey}>
                        <Table.Summary.Cell index={baseIndex} align="right">
                          {subtotal == null
                            ? ALIAS_FOR_NULL_VALUES
                            : Number.isInteger(subtotal)
                              ? subtotal.toLocaleString()
                              : subtotal.toFixed(2)}
                        </Table.Summary.Cell>
                        {showTrendColumns && (
                          <Table.Summary.Cell
                            index={baseIndex + 1}
                            align="right"
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              -
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {isSummableMetric(metric) && (
                          <Table.Summary.Cell
                            index={baseIndex + volumeOffset}
                            align="right"
                          >
                            100.0%
                          </Table.Summary.Cell>
                        )}
                        {isSummableMetric(metric) && hasGrouping && (
                          <Table.Summary.Cell
                            index={baseIndex + parentOffset}
                            align="right"
                          >
                            -
                          </Table.Summary.Cell>
                        )}
                      </React.Fragment>
                    )
                  })}
                </Table.Summary.Row>
              )}

              {/* Total row - entire dataset aggregates */}
              <Table.Summary.Row
                className="summary-row"
                style={{ fontWeight: '600' }}
              >
                <Table.Summary.Cell index={0}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    Total
                    <Tooltip content="Total for the entire dataset">
                      <InfoCircleOutlined
                        style={{ fontSize: 12, color: '#999' }}
                      />
                    </Tooltip>
                  </span>
                </Table.Summary.Cell>
                {breakdownNames.length > 1 && (
                  <Table.Summary.Cell
                    index={1}
                    colSpan={breakdownNames.length - 1}
                  />
                )}
                {selectedMetrics.map((metric, index) => {
                  const metricKey = `metric_${index}`
                  const total = parentTotals[metricKey]
                  const previousTotal = previousPeriodTotals[metricKey]

                  // Calculate base index for this metric. Per metric the body
                  // emits: value (+ trend when shown) (+ % Volume when summable)
                  // (+ % Volume (Parent) when summable && grouping). The trend
                  // column is dropped when showTrendColumns is false (standalone
                  // renders with no comparison data), so the offsets shift down.
                  const trendCols = showTrendColumns ? 1 : 0
                  let baseIndex = breakdownNames.length
                  for (let i = 0; i < index; i++) {
                    const prevMetric = selectedMetrics[i]
                    if (prevMetric) {
                      const summableCols = (hasGrouping ? 3 : 2) + trendCols
                      baseIndex += isSummableMetric(prevMetric)
                        ? summableCols
                        : 1 + trendCols
                    }
                  }
                  // Offsets within this metric's column block.
                  const volumeOffset = 1 + trendCols
                  const parentOffset = 2 + trendCols

                  return (
                    <React.Fragment key={metricKey}>
                      <Table.Summary.Cell index={baseIndex} align="right">
                        {total == null
                          ? ALIAS_FOR_NULL_VALUES
                          : Number.isInteger(total)
                            ? total.toLocaleString()
                            : total.toFixed(2)}
                      </Table.Summary.Cell>
                      {showTrendColumns && (
                        <Table.Summary.Cell index={baseIndex + 1} align="right">
                          {total == null ? (
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              -
                            </span>
                          ) : (
                            <TrendIndicator
                              current={total}
                              previous={previousTotal ?? null}
                              metric={metric}
                              isLoading={isPreviousLoading ?? false}
                              previousPeriodDates={previousPeriodDates ?? null}
                            />
                          )}
                        </Table.Summary.Cell>
                      )}
                      {isSummableMetric(metric) && (
                        <Table.Summary.Cell
                          index={baseIndex + volumeOffset}
                          align="right"
                        >
                          100.0%
                        </Table.Summary.Cell>
                      )}
                      {isSummableMetric(metric) && hasGrouping && (
                        <Table.Summary.Cell
                          index={baseIndex + parentOffset}
                          align="right"
                        >
                          -
                        </Table.Summary.Cell>
                      )}
                    </React.Fragment>
                  )
                })}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
      <PaginationFooter>
        <TablePaginationFooter
          total={
            numBreakdowns > 1 && !hasGrouping ? totalGroups : sortedData.length
          }
          page={
            numBreakdowns > 1 && !hasGrouping ? currentPage : antdCurrentPage
          }
          pageSize={
            numBreakdowns > 1 && !hasGrouping ? groupPageSize : antdPageSize
          }
          onPageChange={
            numBreakdowns > 1 && !hasGrouping
              ? setCurrentPage
              : setAntdCurrentPage
          }
          onPageSizeChange={
            numBreakdowns > 1 && !hasGrouping
              ? setGroupPageSize
              : setAntdPageSize
          }
        />
      </PaginationFooter>
    </div>
  )
}
